<template>
  <div class="subflow-panel" v-if="config">
    <!-- ========== 子流程选择 ========== -->
    <details class="prop-group" open>
      <summary class="group-title"><i class="fas fa-sitemap"></i> 子流程选择</summary>
      <div class="prop-row">
        <label>目标子流程</label>
        <select v-model="config.processGuid" @change="onProcessChange" class="prop-input" :disabled="processesLoading">
          <option value="">-- 请选择 --</option>
          <option v-for="p in processList" :key="p.guid" :value="p.guid">
            {{ p.processName }}
          </option>
        </select>
      </div>
      <div class="prop-row" v-if="processesLoading" style="color:#9ca3af;font-size:11px;padding-top:0">
        <i class="fas fa-spinner fa-pulse"></i> 正在加载流程列表...
      </div>
      <div class="prop-row" v-if="processesError" style="color:#ef4444;font-size:11px;padding-top:0">
        <i class="fas fa-exclamation-triangle"></i> {{ processesError }}
      </div>
      <div class="prop-row" v-if="config.processGuid">
        <label>起始节点</label>
        <select v-model.number="config.startNodeId" @change="emitChange" class="prop-input">
          <option v-for="n in nodeList" :key="n.id" :value="n.id">
            {{ n.id }} - {{ n.name }} ({{ nodeTypeName(n.nodeType) }})
          </option>
        </select>
      </div>
      <div class="prop-row" v-if="config.processGuid">
        <label>第一个审批节点</label>
        <select v-model.number="config.approverNodeId" @change="emitChange" class="prop-input">
          <option :value="0">-- 自动找下一人工节点 --</option>
          <option v-for="n in nodeList.filter(n => n.nodeType === 1 || n.nodeType === 35)" :key="n.id" :value="n.id">
            {{ n.id }} - {{ n.name }}
          </option>
        </select>
      </div>
    </details>

    <!-- ========== 发起人 ========== -->
    <details class="prop-group" open>
      <summary class="group-title"><i class="fas fa-user"></i> 发起人</summary>
      <div class="prop-row">
        <label>模式</label>
        <select v-model="config.initiator.mode" @change="emitChange" class="prop-input">
          <option value="inherit">继承父流程发起人</option>
          <option value="custom">自定义账号</option>
        </select>
      </div>
      <template v-if="config.initiator.mode === 'custom'">
        <div class="prop-row">
          <label>账号</label>
          <input v-model="config.initiator.account" @input="emitChange" class="prop-input" placeholder="发起人账号" />
        </div>
        <div class="prop-row">
          <label>姓名</label>
          <input v-model="config.initiator.displayName" @input="emitChange" class="prop-input" placeholder="发起人姓名" />
        </div>
      </template>
    </details>

    <!-- ========== 流水号 ========== -->
    <details class="prop-group" open>
      <summary class="group-title"><i class="fas fa-hashtag"></i> 流水号</summary>
      <div class="prop-row">
        <label>生成模式</label>
        <select v-model="config.serialNum.mode" @change="onSerialModeChange" class="prop-input">
          <option value="inherit">继承父任务流水号</option>
          <option value="prefix-date-seq">前缀 + 日期 + 流水号</option>
          <option value="custom">自定义表达式</option>
        </select>
      </div>

      <template v-if="config.serialNum.mode === 'prefix-date-seq'">
        <div class="prop-row">
          <label>前缀</label>
          <input v-model="config.serialNum.prefix" @input="emitChange" class="prop-input" placeholder="如 QMSTH-" />
        </div>
        <div class="prop-row">
          <label>日期格式</label>
          <select v-model="config.serialNum.dateFormat" @change="emitChange" class="prop-input">
            <option value="yyyyMMdd">年月日 (20260730)</option>
            <option value="yyyyMM">年月 (202607)</option>
            <option value="yyyy">年 (2026)</option>
            <option value="">不使用</option>
          </select>
        </div>
        <div class="prop-row">
          <label>流水号位数</label>
          <input v-model.number="config.serialNum.seqLength" @input="emitChange" type="number" min="1" max="10" class="prop-input" />
        </div>
        <div class="prop-row">
          <label>查最大流水号表</label>
          <input v-model="config.serialNum.seqTable" @input="emitChange" class="prop-input" placeholder="留空则使用系统表" />
        </div>
        <div class="prop-row">
          <label>流水号字段</label>
          <input v-model="config.serialNum.seqField" @input="emitChange" class="prop-input" placeholder="如 record_No" />
        </div>
        <div class="prop-row">
          <label>预览</label>
          <div class="preview-box">
            <span class="preview-label">{{ config.serialNum.prefix || '' }}{{ serialPreview.datePart }}<span class="preview-seq">{{ serialPreview.seqStr || '0001' }}</span></span>
            <button class="btn-link" @click="refreshPreview"><i class="fas fa-sync"></i> 刷新</button>
          </div>
        </div>
      </template>

      <template v-if="config.serialNum.mode === 'custom'">
        <div class="prop-row">
          <label>模板</label>
          <input v-model="config.serialNum.customTemplate" @input="emitChange" class="prop-input" placeholder="如 QMSTH-{date:yyyyMMdd}-{parent.taskId}" />
        </div>
        <div class="hint-block">
          支持变量：<code>{date:yyyyMMdd}</code> <code>{date:yyyyMM}</code> <code>{date:yyyy}</code>
          <code>{parent.SerialNum}</code> <code>{parent.taskId}</code>
        </div>
      </template>
    </details>

    <!-- ========== 任务摘要 ========== -->
    <details class="prop-group">
      <summary class="group-title"><i class="fas fa-comment"></i> 任务摘要</summary>
      <div class="prop-row">
        <label>模式</label>
        <select v-model="config.description.mode" @change="emitChange" class="prop-input">
          <option value="inherit">继承父任务描述</option>
          <option value="template">模板拼接</option>
        </select>
      </div>
      <div class="prop-row full" v-if="config.description.mode === 'template'">
        <label>模板</label>
        <input v-model="config.description.template" @input="emitChange" class="prop-input" placeholder="如 {parent.Description} - 子流程自动发起" />
      </div>
      <div class="hint-block">
        支持变量：<code>{parent.Description}</code> <code>{parent.SerialNum}</code> <code>{serialNum}</code> <code>{initiator}</code>
      </div>
    </details>

    <!-- ========== 审批人 ========== -->
    <details class="prop-group">
      <summary class="group-title"><i class="fas fa-user-check"></i> 审批人</summary>
      <div class="prop-row">
        <label>模式</label>
        <select v-model="config.approver.mode" @change="emitChange" class="prop-input">
          <option value="process-native">使用子流程本身的审批人配置</option>
          <option value="custom">本次自定义（指定账号/组织/角色）</option>
        </select>
      </div>
      <template v-if="config.approver.mode === 'custom'">
        <div class="prop-row">
          <label>来源类型</label>
          <select v-model="config.approver.customSource" @change="emitChange" class="prop-input">
            <option value="user">用户</option>
            <option value="org">组织</option>
            <option value="role">角色</option>
          </select>
        </div>
        <div class="custom-list">
          <div v-for="(item, idx) in config.approver.customList" :key="idx" class="custom-item">
            <input v-model="item.code" @input="emitChange" class="prop-input" placeholder="账号/编码" />
            <input v-model="item.name" @input="emitChange" class="prop-input" placeholder="姓名（可选）" />
            <button class="btn-icon" @click="removeApprover(idx)"><i class="fas fa-times"></i></button>
          </div>
          <button class="btn-add-line" @click="addApprover"><i class="fas fa-plus"></i> 添加审批人</button>
        </div>
        <div class="hint-block">
          支持特殊变量：<code>{parent.initiator}</code> <code>{parent.initiatorName}</code>
          <code>{parent.taskId}</code> <code>{parent.serialNum}</code>
        </div>
      </template>
    </details>

    <!-- ========== 字段映射 ========== -->
    <details class="prop-group">
      <summary class="group-title"><i class="fas fa-exchange-alt"></i> 字段映射</summary>
      <div class="hint-block">
        无需手写 INSERT。勾选目标表 + 字段 + 选择数据来源即可生成写入逻辑。
      </div>
      <div class="table-mappings">
        <div v-for="(tbl, tIdx) in config.fieldMapping.tables" :key="tIdx" class="mapping-card">
          <div class="mapping-head">
            <input v-model="tbl.table" @input="emitChange" class="prop-input" placeholder="目标表名 (如 UT_QMSTH)" />
            <select v-model="tbl.writeMode" @change="emitChange" class="prop-input" style="width:120px">
              <option value="insert">INSERT</option>
              <option value="upsert">UPSERT（存在则更新）</option>
            </select>
            <button class="btn-icon" @click="removeTable(tIdx)"><i class="fas fa-trash"></i></button>
          </div>
          <template v-if="tbl.writeMode === 'upsert'">
            <div class="prop-row">
              <label>关键字段</label>
              <input v-model="tbl.keyFieldsText" @input="updateKeyFields(tIdx)" class="prop-input" placeholder="逗号分隔，如 record_No" />
            </div>
          </template>
          <div class="fields-table">
            <div class="fields-hd">
              <span>目标字段</span>
              <span>来源</span>
              <span style="width:32px"></span>
            </div>
            <div v-for="(f, fIdx) in tbl.fields" :key="fIdx" class="fields-row">
              <input v-model="f.target" @input="emitChange" class="prop-input" placeholder="如 TH001" />
              <select v-model="f.source" @change="emitChange" class="prop-input source-select">
                <optgroup label="内置">
                  <option value="serialNum">本次生成的流水号</option>
                  <option value="newTaskId">新任务 ID</option>
                  <option value="now">当前时间</option>
                  <option value="initiator">父任务发起人</option>
                  <option value="initiatorName">父任务发起人姓名</option>
                  <option value="parent.taskId">父任务 ID</option>
                  <option value="parent.serialNum">父任务流水号</option>
                  <option value="parent.description">父任务描述</option>
                </optgroup>
                <optgroup label="父表单字段" v-if="parentFields.length">
                  <option v-for="p in parentFields" :key="p" :value="'parent.' + p">{{ p }}</option>
                </optgroup>
                <optgroup label="字面量">
                  <option value="literal:">字面量（手动填后写"literal:值"）</option>
                </optgroup>
              </select>
              <button class="btn-icon" @click="removeField(tIdx, fIdx)"><i class="fas fa-times"></i></button>
            </div>
            <button class="btn-add-line" @click="addField(tIdx)"><i class="fas fa-plus"></i> 添加字段</button>
          </div>
        </div>
        <button class="btn-add-table" @click="addTable"><i class="fas fa-plus-circle"></i> 添加目标表</button>
      </div>
    </details>

    <!-- ========== 额外 SQL ========== -->
    <details class="prop-group">
      <summary class="group-title"><i class="fas fa-terminal"></i> 额外 SQL</summary>
      <div class="hint-block">
        需要执行其它 SQL 时使用（如更新前置单据状态、写入关联表）。支持参数占位符。
      </div>
      <div v-for="(s, idx) in config.extraSqlList" :key="idx" class="extra-sql-row">
        <div class="extra-sql-head">
          <input v-model="s.description" @input="emitChange" class="prop-input" placeholder="用途说明（可选）" style="flex:1" />
          <label class="cb-inline">
            <input type="checkbox" v-model="s.enabled" @change="emitChange" /> 启用
          </label>
          <button class="btn-icon" @click="removeExtraSql(idx)"><i class="fas fa-trash"></i></button>
        </div>
        <textarea v-model="s.sql" @input="emitChange" rows="3" class="prop-input code-input" placeholder="UPDATE UT_QMSTF SET STF08={newTaskId} WHERE TF001=@parentTaskId"></textarea>
      </div>
      <button class="btn-add-line" @click="addExtraSql"><i class="fas fa-plus"></i> 添加 SQL 语句</button>
      <div class="hint-block">
        可用占位符：<code>{newTaskId}</code> <code>{serialNum}</code> <code>{initiator}</code>
        <code>{parent.taskId}</code> <code>{parent.SerialNum}</code>
      </div>
    </details>

    <!-- ========== 对应关系 ========== -->
    <details class="prop-group">
      <summary class="group-title"><i class="fas fa-link"></i> 对应关系</summary>
      <div class="prop-row">
        <label>启用关联</label>
        <label class="cb-inline">
          <input type="checkbox" v-model="config.relation.enabled" @change="emitChange" /> 启用父子流程对应关系
        </label>
      </div>
      <template v-if="config.relation.enabled">
        <div class="prop-row">
          <label>关系类型</label>
          <select v-model="config.relation.type" @change="emitChange" class="prop-input">
            <option value="preDocument">前置单据（主→子）</option>
            <option value="relate">关联单据</option>
            <option value="none">不记录</option>
          </select>
        </div>
        <div class="prop-row">
          <label>父表单表名</label>
          <input v-model="config.relation.parentTable" @input="emitChange" class="prop-input" placeholder="如 UT_QMSTF（用于回溯展示）" />
        </div>
        <div class="prop-row">
          <label>父表单关键字段</label>
          <input v-model="config.relation.parentKeyField" @input="emitChange" class="prop-input" placeholder="如 TF001" />
        </div>
        <div class="prop-row">
          <label>父任务ID写入子流程</label>
          <label class="cb-inline">
            <input type="checkbox" v-model="config.relation.storeInChild" @change="emitChange" /> 启用
          </label>
        </div>
        <div class="prop-row" v-if="config.relation.storeInChild">
          <label>子流程字段名</label>
          <input v-model="config.relation.childTaskIdField" @input="emitChange" class="prop-input" placeholder="如 RelatedTaskId" />
        </div>
      </template>
    </details>
  </div>
</template>

<script setup>
import { reactive, watch, ref, computed, onMounted } from 'vue'
import { listSubFlowProcesses, getSubFlowNodes, previewSerialNum } from '../../../api/subflow'

const props = defineProps({
  config: { type: Object, required: true }
})
const emit = defineEmits(['update:config', 'save'])

// 兜底默认值
const defaults = () => ({
  processGuid: '', processName: '', startNodeId: 2, approverNodeId: 0,
  initiator: { mode: 'inherit', account: '', displayName: '' },
  serialNum: { mode: 'inherit', prefix: '', dateFormat: 'yyyyMMdd', seqLength: 4, seqTable: '', seqField: '', customTemplate: '' },
  description: { mode: 'inherit', template: '' },
  approver: { mode: 'process-native', customSource: 'user', customList: [] },
  fieldMapping: { tables: [] },
  extraSqlList: [],
  relation: { enabled: true, type: 'preDocument', parentTable: '', parentKeyField: '', storeInChild: true, childTaskIdField: 'RelatedTaskId' }
})

// 规范化 config — 用 watch 替代 onMounted，确保父组件异步设置 subFlow 时也能补全默认值
watch(() => props.config, (val) => {
  if (!val) {
    emit('update:config', defaults())
    return
  }
  // 防止无限循环：如果 config 已包含完整子结构（initiator.mode 存在），说明已初始化完毕
  if (val.initiator && val.initiator.mode !== undefined) return
  const merged = { ...defaults(), ...val }
  merged.initiator = { ...defaults().initiator, ...(val.initiator || {}) }
  merged.serialNum = { ...defaults().serialNum, ...(val.serialNum || {}) }
  merged.description = { ...defaults().description, ...(val.description || {}) }
  merged.approver = { ...defaults().approver, ...(val.approver || {}) }
  merged.approver.customList = (val.approver?.customList || []).map(x => ({ code: '', name: '', ...x }))
  merged.fieldMapping = { ...defaults().fieldMapping, ...(val.fieldMapping || {}) }
  merged.fieldMapping.tables = (val.fieldMapping?.tables || []).map(t => ({
    table: '', writeMode: 'insert', keyFields: [], ...t,
    keyFieldsText: (t.keyFields || []).join(',')
  }))
  merged.extraSqlList = (val.extraSqlList || []).map(x => ({ enabled: true, sql: '', description: '', ...x }))
  merged.relation = { ...defaults().relation, ...(val.relation || {}) }
  emit('update:config', merged)
}, { immediate: true })

const processList = ref([])
const nodeList = ref([])
const parentFields = ref([])
const processesLoading = ref(false)
const processesError = ref('')

const nodeTypeName = (t) => ({ 0: '开始', 1: '审批', 2: '条件', 35: '子流程', 42: 'SQL', 51: 'WebApi' }[t] || '节点')

const serialPreview = reactive({ datePart: '', seqStr: '' })

async function loadProcesses() {
  processesLoading.value = true
  processesError.value = ''
  try {
    const res = await listSubFlowProcesses()
    // 后端返回 { resultType: true/false, data: [...], msg: '...' }
    // normalizeKeys 已将 PascalCase → camelCase
    if (res && res.resultType === false) {
      processesError.value = res.msg || '获取流程列表失败'
      console.error('[SubFlow] 接口返回失败:', res.msg)
      return
    }
    // data 字段：可能直接是数组，也可能包裹在 data 中
    let list = res?.data ?? res
    if (!Array.isArray(list) && list?.data) list = list.data
    if (!Array.isArray(list)) {
      console.warn('[SubFlow] 流程列表数据格式异常:', res)
      list = []
    }
    processList.value = list
    console.log('[SubFlow] 流程列表加载成功, 数量:', list.length)
  } catch (e) {
    console.error('[SubFlow] 加载流程列表失败:', e)
    processesError.value = '网络请求失败，请检查后端服务'
  } finally {
    processesLoading.value = false
  }
}
async function loadNodes(processGuid) {
  if (!processGuid) { nodeList.value = []; return }
  try {
    const res = await getSubFlowNodes(processGuid)
    if (res && res.resultType === false) {
      console.error('[SubFlow] 获取节点失败:', res.msg)
      nodeList.value = []
      return
    }
    let list = res?.data ?? res
    if (!Array.isArray(list) && list?.data) list = list.data
    nodeList.value = Array.isArray(list) ? list : []
  } catch (e) { console.error('[SubFlow] 加载节点失败:', e); nodeList.value = [] }
}

async function onProcessChange() {
  // 同步 processName
  const p = processList.value.find(x => x.guid === props.config.processGuid)
  if (p) props.config.processName = p.processName
  await loadNodes(props.config.processGuid)
  emitChange()
}

async function refreshPreview() {
  if (props.config.serialNum.mode !== 'prefix-date-seq') return
  const cfg = { ...props.config.serialNum }
  try {
    const res = await previewSerialNum(cfg)
    const s = res.data?.Data?.serialNum ?? res.data?.serialNum ?? ''
    serialPreview.datePart = props.config.serialNum.dateFormat ? new Date().toISOString().slice(0, 10).replace(/-/g, '') : ''
    serialPreview.seqStr = s ? s.slice(-cfg.seqLength) : ''
  } catch (e) {
    serialPreview.datePart = ''
    serialPreview.seqStr = ''
  }
}

function onSerialModeChange() { emitChange(); if (props.config.serialNum.mode === 'prefix-date-seq') refreshPreview() }

function emitChange() { emit('update:config', { ...props.config }); emit('save') }

function addApprover() { props.config.approver.customList.push({ code: '', name: '' }); emitChange() }
function removeApprover(idx) { props.config.approver.customList.splice(idx, 1); emitChange() }
function addTable() { props.config.fieldMapping.tables.push({ table: '', writeMode: 'insert', keyFields: [], fields: [] }); emitChange() }
function removeTable(tIdx) { props.config.fieldMapping.tables.splice(tIdx, 1); emitChange() }
function addField(tIdx) { props.config.fieldMapping.tables[tIdx].fields.push({ target: '', source: '' }); emitChange() }
function removeField(tIdx, fIdx) { props.config.fieldMapping.tables[tIdx].fields.splice(fIdx, 1); emitChange() }
function updateKeyFields(tIdx) {
  const tbl = props.config.fieldMapping.tables[tIdx]
  tbl.keyFields = (tbl.keyFieldsText || '').split(',').map(s => s.trim()).filter(Boolean)
  emitChange()
}
function addExtraSql() { props.config.extraSqlList.push({ enabled: true, sql: '', description: '' }); emitChange() }
function removeExtraSql(idx) { props.config.extraSqlList.splice(idx, 1); emitChange() }

onMounted(() => {
  loadProcesses()
  if (props.config?.processGuid) loadNodes(props.config.processGuid)
})
</script>

<style scoped>
.subflow-panel { margin: 0; }

/* ===== 分组容器 ===== */
.prop-group {
  border-bottom: 1px solid #f3f4f6;
  transition: background .2s;
}
.prop-group[open] { background: #fafbfd; }
.prop-group[open] > .group-title {
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(180deg, #f8fafc, #f1f5f9);
}
.group-title {
  padding: 9px 14px;
  cursor: pointer;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 7px;
  list-style: none;
  user-select: none;
  transition: background .15s;
}
.group-title::-webkit-details-marker { display: none; }
.group-title:hover { background: #f0fdf4; }
.group-title i { width: 16px; text-align: center; color: #10b981; font-size: 13px; }

/* ===== 属性行 ===== */
.prop-row {
  display: flex;
  align-items: center;
  padding: 5px 14px;
  gap: 10px;
}
.prop-row.full {
  flex-wrap: wrap;
  align-items: flex-start;
}
.prop-row label {
  flex: 0 0 80px;
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  font-weight: 500;
}
.prop-row.full > label {
  flex: 0 0 100%;
  margin-bottom: 3px;
  font-weight: 600;
  color: #475569;
}

.prop-input {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  outline: none;
  min-width: 0;
  background: #fff;
  color: #1f2937;
  transition: border-color .2s, box-shadow .2s;
  box-sizing: border-box;
}
.prop-input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, .12);
}
.prop-input::placeholder { color: #cbd5e1; }
select.prop-input { cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239ca3af'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 8px center; padding-right: 26px; }
textarea.prop-input { resize: vertical; min-height: 60px; font-family: inherit; }

.prop-check {
  width: 16px; height: 16px; cursor: pointer; accent-color: #10b981;
  margin: 0; flex-shrink: 0;
}

/* ===== 提示块 ===== */
.hint-block {
  background: linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%);
  border-left: 3px solid #38bdf8;
  padding: 7px 12px;
  font-size: 11px;
  color: #475569;
  border-radius: 0 6px 6px 0;
  margin: 6px 14px 10px;
  line-height: 1.7;
}
.hint-block code {
  background: #fff;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: #0369a1;
  border: 1px solid #bae6fd;
  margin-right: 5px;
  font-family: 'Consolas', monospace;
}

/* ===== 预览区域 ===== */
.preview-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 12px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px dashed #a7f3d0;
  border-radius: 6px;
  flex: 1;
}
.preview-label {
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 14px;
  color: #047857;
  font-weight: 500;
  letter-spacing: 0.5px;
}
.preview-seq {
  color: #ea580c;
  font-weight: 700;
  font-size: 14px;
  background: #fff7ed;
  padding: 1px 4px;
  border-radius: 3px;
}

.btn-link {
  background: none; border: none;
  color: #6b7280; cursor: pointer;
  font-size: 11px; padding: 3px 6px;
  border-radius: 4px;
  transition: all .15s;
  flex-shrink: 0;
}
.btn-link:hover { color: #059669; background: #d1fae5; }
.btn-link i { margin-right: 3px; }

.cb-inline {
  display: inline-flex; align-items: center;
  gap: 5px; font-size: 12px; cursor: pointer;
  color: #475569;
}
.cb-inline input[type="checkbox"] { accent-color: #10b981; }

/* ===== 图标按钮 ===== */
.btn-icon {
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  padding: 5px 7px;
  border-radius: 5px;
  font-size: 12px;
  transition: all .15s;
  flex-shrink: 0;
}
.btn-icon:hover {
  background: #fee2e2;
  color: #ef4444;
}

/* ===== 添加按钮 ===== */
.btn-add-line {
  background: linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px dashed #86efac;
  color: #059669;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  margin: 8px 0 4px;
  width: 100%;
  transition: all .2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.btn-add-line:hover {
  background: #10b981;
  color: #fff;
  border-color: #10b981;
  box-shadow: 0 2px 8px rgba(16, 185, 129, .25);
  transform: translateY(-1px);
}

.btn-add-table {
  background: linear-gradient(180deg, #eff6ff 0%, #eef2ff 100%);
  border: 2px dashed #93c5fd;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  width: 100%;
  margin-top: 6px;
  transition: all .2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.btn-add-table:hover {
  background: #3b82f6;
  color: #fff;
  border-color: #3b82f6;
  box-shadow: 0 3px 10px rgba(59, 130, 246, .25);
  transform: translateY(-1px);
}

/* ===== 审批人自定义列表 ===== */
.custom-list { padding: 0 14px 4px; }
.custom-item {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  align-items: center;
}
.custom-item input { flex: 1; }

/* ===== 字段映射 - 表卡片 ===== */
.table-mappings { padding: 0 14px 6px; }
.mapping-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .05);
  transition: border-color .2s, box-shadow .2s;
}
.mapping-card:hover {
  border-color: #a5b4fc;
  box-shadow: 0 3px 12px rgba(99, 102, 241, .08);
}
.mapping-head {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
}
.mapping-head input:first-child { flex: 1; }
.mapping-head select { flex-shrink: 0; }

/* ===== 字段表 ===== */
.fields-table {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
}
.fields-hd, .fields-row {
  display: grid;
  grid-template-columns: 1fr 2fr 32px;
  gap: 8px;
  align-items: center;
  padding: 5px 0;
}
.fields-hd {
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  padding: 5px 6px;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.fields-row { padding: 4px 2px; }
.fields-row input, .fields-row select { width: 100%; }
.source-select { font-size: 11px; }

/* ===== 额外 SQL ===== */
.extra-sql-row {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  margin: 8px 14px;
  transition: border-color .2s;
}
.extra-sql-row:hover { border-color: #c4b5fd; }
.extra-sql-head {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.code-input {
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
  background: #fafbfc;
}

/* ===== Group title icons colors by section ===== */
.prop-group:nth-child(1) > .group-title i { color: #10b981; }  /* 子流程选择 - green */
.prop-group:nth-child(2) > .group-title i { color: #f59e0b; }  /* 发起人 - amber */
.prop-group:nth-child(3) > .group-title i { color: #6366f1; }  /* 流水号 - indigo */
.prop-group:nth-child(4) > .group-title i { color: #ec4899; }  /* 任务摘要 - pink */
.prop-group:nth-child(5) > .group-title i { color: #06b6d4; }  /* 审批人 - cyan */
.prop-group:nth-child(6) > .group-title i { color: #8b5cf6; }  /* 字段映射 - violet */
.prop-group:nth-child(7) > .group-title i { color: #f97316; }  /* 额外SQL - orange */
.prop-group:nth-child(8) > .group-title i { color: #14b8a6; }  /* 对应关系 - teal */
</style>